/**
 * @file transformGrants.js
 * @description Utility functions for transforming and evaluating grant rules with logical conditions.
 * Uses expr-eval for CSP-compliant expression evaluation.
 */

import { Parser } from "expr-eval";

/**
 * Converts a custom expression syntax to expr-eval compatible format.
 *
 * @param {string} expression - The expression in custom syntax
 * @returns {string} The transformed expression compatible with expr-eval
 */
export function transformExpression(expression) {
    if (!expression) return "";

    // Convert operators
    let normalized = expression
        .replace(/\bEQ\b/gi, "==")
        .replace(/\bNE\b/gi, "!=")
        .replace(/\bGT\b/gi, ">")
        .replace(/\bLT\b/gi, "<")
        .replace(/\bGE\b/gi, ">=")
        .replace(/\bLE\b/gi, "<=")
        .replace(/\bAND\b/gi, "and")
        .replace(/\bIN\b/gi, "in")
        .replace(/\bNOT\b/gi, "not")
        .replace(/\bOR\b/gi, "or");
    return normalized;
}

/**
 * Transforms an array of grant rules with logical conditions into an object
 * mapping permissions to their evaluated condition results.
 *
 * @param {Array<{ condition: string, permissions: string[] }>} grants - The input grant rules
 * @param {Object} activeCompany - The active company data to evaluate conditions against
 * @returns {Object<string, boolean>} An object mapping permission names to their evaluated condition results
 */
function transformGrants(grants, activeCompany) {
    if (!Array.isArray(grants)) return {};

    const companyId = activeCompany?.scsCompanyId;
    const parser = new Parser();
    return grants.reduce((result, grant) => {
        if (grant && Array.isArray(grant.permissions)) {
            const { condition, permissions } = grant;
            let evaluatedCondition = true;
            if (condition) {
                try {
                    const transformed = transformExpression(condition);
                    const expr = parser.parse(transformed);
                    evaluatedCondition = expr.evaluate({ companyId: companyId });
                } catch {
                    // Default to true on error for safety
                    evaluatedCondition = false;
                    // eslint-disable-next-line
                    console.error("Error evaluating condition:", condition);
                }
            }

            permissions.forEach(permission => {
                if (permission) {
                    result[permission] =
                        result[permission] === undefined
                            ? evaluatedCondition
                            : result[permission] && evaluatedCondition;
                }
            });
        }
        return result;
    }, {});
}

export default transformGrants;
